import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useState, type FormEvent } from "react";
import { Info } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { SeletorMinisterios, type OpcaoMinisterio } from "@/shared/components/SeletorMinisterios";
import type {
  ComunicadoComRelacoes,
  DadosComunicado,
} from "@/modules/comunicados/services/comunicadosService";
import {
  DIAS_DE_PRAZO,
  dataCurtaNoFuso,
  diasParaApagar,
  prazoJaTerminou,
} from "@/modules/comunicados/utils/prazo";

const LIMITE_TITULO = 100;
const LIMITE_TEXTO = 1000;

const CLASSE_INPUT =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
const CLASSE_LABEL = "text-sm font-medium text-text";
const CLASSE_OPCAO =
  "flex cursor-pointer items-start gap-2.5 rounded-xl border border-border bg-surface px-3 py-2.5 has-[:checked]:border-primary has-[:checked]:bg-primary/5 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary";

/** Tira caracteres de controle que o banco recusa (mantém tab, quebra de linha e retorno no texto). */
function limparControle(texto: string, manterQuebras: boolean): string {
  let saida = "";
  for (const caractere of texto) {
    const codigo = caractere.codePointAt(0) ?? 0;
    const controle = codigo < 32 || codigo === 127;
    const permitido = manterQuebras && (codigo === 9 || codigo === 10 || codigo === 13);
    saida += controle && !permitido ? (manterQuebras ? "" : " ") : caractere;
  }
  return saida;
}

interface FormularioComunicadoProps {
  /** Presente = edição; ausente = novo comunicado. */
  valoresIniciais?: ComunicadoComRelacoes;
  ministerios: OpcaoMinisterio[];
  carregando: boolean;
  erro?: string | null;
  onSubmit: (dados: DadosComunicado) => void;
  onCancelar: () => void;
}

/**
 * Formulário de novo/edição de comunicado (protótipo v4 aprovado). Publica
 * direto, mas pede confirmação. Ao editar, dá para mudar destino e prazo e a
 * contagem de 30 dias NÃO reinicia. Se os 30 dias de um comunicado "mantido"
 * já passaram, ligar "apagar sozinho" o apagaria na hora — a opção fica
 * bloqueada com o motivo (regra D7; o banco também recusa).
 */
export function FormularioComunicado({
  valoresIniciais,
  ministerios,
  carregando,
  erro,
  onSubmit,
  onCancelar,
}: FormularioComunicadoProps) {
  const editando = !!valoresIniciais;
  const [titulo, setTitulo] = useState(valoresIniciais?.titulo ?? "");
  const [corpo, setCorpo] = useState(valoresIniciais?.corpo ?? "");
  const [paraMinisterios, setParaMinisterios] = useState(!!valoresIniciais?.destinos.length);
  const [ministerioIds, setMinisterioIds] = useState<string[]>(
    valoresIniciais?.destinos.map((d) => d.ministerioId) ?? [],
  );
  const [apagarAutomaticamente, setApagarAutomaticamente] = useState(
    valoresIniciais?.apagar_automaticamente ?? true,
  );
  const [confirmando, setConfirmando] = useState(false);

  // D7: era "manter até alguém apagar" e os 30 dias já passaram → não dá para ligar o prazo.
  const prazoBloqueado =
    editando && !valoresIniciais.apagar_automaticamente && prazoJaTerminou(valoresIniciais.contagem_inicio_em);

  const nomesEscolhidos = ministerioIds
    .map((id) => ministerios.find((m) => m.id === id)?.nome)
    .filter((nome): nome is string => !!nome);

  const podeEnviar =
    !!titulo.trim() && !!corpo.trim() && (!paraMinisterios || ministerioIds.length > 0) && !carregando;

  const quemVe = !paraMinisterios
    ? "Todos os membros da igreja poderão ler."
    : nomesEscolhidos.length
      ? `Só os membros de ${nomesEscolhidos.join(", ")} poderão ler.`
      : "Escolha ao menos um ministério.";

  const quandoApaga = (() => {
    if (!apagarAutomaticamente) return "Fica publicado até alguém apagar.";
    if (editando) {
      const dias = diasParaApagar(valoresIniciais.contagem_inicio_em);
      return dias > 0
        ? `Será apagado em ${dias} ${dias === 1 ? "dia" : "dias"}, a contar da publicação.`
        : "Os 30 dias já passaram. Será apagado na próxima limpeza.";
    }
    const dia = new Date();
    dia.setDate(dia.getDate() + DIAS_DE_PRAZO);
    return `Será apagado em ${DIAS_DE_PRAZO} dias, em ${dataCurtaNoFuso(dia)}.`;
  })();

  function montarDados(): DadosComunicado {
    return {
      titulo: titulo.trim(),
      corpo: corpo.trim(),
      ministerioIds: paraMinisterios ? ministerioIds : [],
      apagarAutomaticamente,
    };
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault();
    if (podeEnviar) setConfirmando(true);
  }

  const mensagemConfirmacao = editando
    ? "A versão publicada será substituída para quem pode ler."
    : `Ele ficará visível para ${
        paraMinisterios ? `os membros de ${nomesEscolhidos.join(", ")}` : "todos os membros da igreja"
      }${apagarAutomaticamente ? " e será apagado em 30 dias." : " e ficará até alguém apagar."}`;

  return (
    <>
      <form onSubmit={aoEnviar} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="comunicado-titulo" className={CLASSE_LABEL}>
            Título
          </label>
          <input
            id="comunicado-titulo"
            type="text"
            value={titulo}
            maxLength={LIMITE_TITULO}
            autoComplete="off"
            onChange={(e) => setTitulo(limparControle(e.target.value, false))}
            className={CLASSE_INPUT}
          />
          <p
            className={`text-right text-xs ${titulo.length >= LIMITE_TITULO ? "font-medium text-warning" : "text-muted"}`}
          >
            {titulo.length}/{LIMITE_TITULO}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="comunicado-texto" className={CLASSE_LABEL}>
            Texto
          </label>
          <textarea
            id="comunicado-texto"
            rows={6}
            value={corpo}
            maxLength={LIMITE_TEXTO}
            onChange={(e) => setCorpo(limparControle(e.target.value, true))}
            className={`${CLASSE_INPUT} resize-none leading-relaxed`}
          />
          <div className="flex justify-between gap-2 text-xs text-muted">
            <span>Texto simples, sem links ou formatação.</span>
            <span className={corpo.length >= LIMITE_TEXTO * 0.9 ? "font-medium text-warning" : ""}>
              {corpo.length}/{LIMITE_TEXTO}
            </span>
          </div>
        </div>

        <fieldset className="flex min-w-0 flex-col gap-2">
          <legend className={`${CLASSE_LABEL} mb-1.5`}>Quem vai ver</legend>
          <label className={CLASSE_OPCAO}>
            <input
              type="radio"
              name="comunicado-destino"
              checked={!paraMinisterios}
              onChange={() => setParaMinisterios(false)}
              className="mt-1 accent-primary"
            />
            <span className="text-sm font-semibold text-text">Toda a igreja</span>
          </label>
          <label className={CLASSE_OPCAO}>
            <input
              type="radio"
              name="comunicado-destino"
              checked={paraMinisterios}
              onChange={() => setParaMinisterios(true)}
              className="mt-1 accent-primary"
            />
            <span>
              <span className="block text-sm font-semibold text-text">Ministérios escolhidos</span>
              <span className="block text-xs text-muted">Só os membros desses ministérios.</span>
            </span>
          </label>
          {paraMinisterios && (
            <SeletorMinisterios
              ministerios={ministerios}
              selecionados={ministerioIds}
              onChange={setMinisterioIds}
              vazioAviso="Nenhum ministério ativo para escolher."
            />
          )}
        </fieldset>

        <fieldset className="flex min-w-0 flex-col gap-2">
          <legend className={`${CLASSE_LABEL} mb-1.5`}>Quando apagar</legend>
          <label className={CLASSE_OPCAO}>
            <input
              type="radio"
              name="comunicado-prazo"
              checked={apagarAutomaticamente}
              disabled={prazoBloqueado}
              onChange={() => setApagarAutomaticamente(true)}
              className="mt-1 accent-primary"
            />
            <span>
              <span className="block text-sm font-semibold text-text">Apagar sozinho em 30 dias</span>
              <span className="block text-xs text-muted">Some para todos 30 dias depois de publicado.</span>
              {prazoBloqueado && (
                <span className="mt-1 block text-xs font-medium text-warning">
                  Indisponível: os 30 dias deste comunicado já terminaram, então ele seria apagado na hora.
                  Para um novo prazo, publique um novo comunicado.
                </span>
              )}
            </span>
          </label>
          <label className={CLASSE_OPCAO}>
            <input
              type="radio"
              name="comunicado-prazo"
              checked={!apagarAutomaticamente}
              onChange={() => setApagarAutomaticamente(false)}
              className="mt-1 accent-primary"
            />
            <span>
              <span className="block text-sm font-semibold text-text">Manter até alguém apagar</span>
              <span className="block text-xs text-muted">Fica publicado até um líder ou gestor apagar.</span>
            </span>
          </label>
        </fieldset>

        <p className="flex items-start gap-2 rounded-xl bg-primary/10 px-3 py-2.5 text-[13px] text-text">
          <Info size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
          <span>
            {quemVe} {quandoApaga}
          </span>
        </p>

        {erro && !confirmando && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {erro}
          </p>
        )}

        <div className="flex gap-2.5">
          <BotaoVoltar onClick={onCancelar} disabled={carregando} className="flex-1" />
          <Button type="submit" disabled={!podeEnviar} className="flex-1">
            {editando ? "Salvar alterações" : "Publicar"}
          </Button>
        </div>
      </form>

      <ModalConfirmacao
        aberto={confirmando}
        variante="padrao"
        titulo={editando ? "Salvar alterações?" : "Publicar comunicado?"}
        mensagem={mensagemConfirmacao}
        textoBotaoConfirmar={editando ? "Salvar" : "Publicar"}
        carregando={carregando}
        erro={erro}
        onConfirmar={() => onSubmit(montarDados())}
        onCancelar={() => setConfirmando(false)}
      />
    </>
  );
}
